import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from './AuthContext';

// Porte le "projet actif" pour toute l'appli — aujourd'hui il n'y a qu'un
// seul projet, donc il est sélectionné automatiquement au démarrage. Le jour
// où plusieurs projets existeront, un vrai sélecteur pourra être ajouté ici
// sans que les pages qui consomment ce contexte n'aient à changer.
const ProjetContext = createContext(null);

export const ProjetProvider = ({ children }) => {
  const [projets, setProjets] = useState([]);
  const [projetActifId, setProjetActifId] = useState(null);
  const [chargement, setChargement] = useState(true);
  const location = useLocation();
  const { utilisateur } = useAuth();
  const dernierUtilisateurId = useRef(undefined);

  useEffect(() => {
    chargerProjets();
  }, []);

  // Reflète le compte réellement connecté à chaque instant : si quelqu'un se
  // déconnecte et se reconnecte avec un autre compte dans le même onglet
  // (sans rechargement complet de la page), l'ancienne liste de projets ne
  // doit jamais rester affichée — sinon un investisseur peut voir les
  // projets d'un compte précédent tant que la liste n'était pas vide.
  useEffect(() => {
    const idActuel = utilisateur?.id ?? null;
    if (dernierUtilisateurId.current === undefined) {
      dernierUtilisateurId.current = idActuel;
      return;
    }
    if (dernierUtilisateurId.current !== idActuel) {
      dernierUtilisateurId.current = idActuel;
      setProjets([]);
      setProjetActifId(null);
      if (idActuel !== null) {
        chargerProjets();
      } else {
        setChargement(false);
      }
    }
  }, [utilisateur?.id]);

  // Recharge automatiquement dès qu'une navigation se produit après une
  // connexion réussie (le token vient d'apparaître, mais ce contexte ne
  // l'avait pas encore vu) — sans ça, l'appli restait bloquée sans projet
  // actif jusqu'à un rechargement manuel complet de la page.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && projets.length === 0) {
      chargerProjets();
    }
  }, [location.pathname]);

  const chargerProjets = async () => {
    // Ne tente jamais de charger les projets sans être connecté — sinon la
    // page de connexion elle-même déclenche un appel qui échoue (401), qui
    // redirige vers /login, qui relance ce même appel : boucle infinie.
    const token = localStorage.getItem('token');
    if (!token) {
      setChargement(false);
      return;
    }
    try {
      const res = await api.get('/projets');
      setProjets(res.data);
      // Reprend le dernier projet actif choisi s'il existe encore, sinon le premier
      const dernierChoisi = localStorage.getItem('projet_actif_id');
      const existeEncore = res.data.find(p => String(p.uuid_id || p.id) === dernierChoisi);
      if (existeEncore) {
        setProjetActifId(existeEncore.uuid_id || existeEncore.id);
      } else if (res.data.length > 0) {
        setProjetActifId(res.data[0].uuid_id || res.data[0].id);
      }
    } catch (error) {
      console.error('Erreur chargement projets:', error);
    } finally {
      setChargement(false);
    }
  };

  const choisirProjet = (id) => {
    setProjetActifId(id);
    localStorage.setItem('projet_actif_id', String(id));
  };

  const projetActif = projets.find(p => (p.uuid_id || p.id) === projetActifId) || null;

  return (
    <ProjetContext.Provider value={{ projets, projetActifId, projetActif, choisirProjet, chargement, rechargerProjets: chargerProjets }}>
      {children}
    </ProjetContext.Provider>
  );
};

export const useProjet = () => useContext(ProjetContext);